"use client";

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Testimonials() {
  const testimonials = [{
    quote: "The environment at Little Acorns is truly nurturing. Our daughter has blossomed in ways we never imagined, developing such a deep love for learning.",
    author: "Sarah M.",
    position: "Parent",
    location: "Parkwoods-Donalda, 2023",
    portrait: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/parent_sarah_portrait.webp",
    portraitSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/parent_sarah_portrait.webp-wvc-srcset",
    portraitSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/parent_sarah_portrait.webp-wvc-sizes"
  }, {
    quote: "We were looking for a school that respected the child's pace. The teachers here are incredibly patient and truly understand the Montessori philosophy.",
    author: "David L.",
    position: "Parent",
    location: "North York, 2024",
    portrait: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/parent_david_portrait.png",
    portraitSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/parent_david_portrait.png-wvc-srcset",
    portraitSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/parent_david_portrait.png-wvc-sizes"
  }, {
    quote: "The sense of community is wonderful. It feels like a second home where our son is encouraged to be independent and kind every single day.",
    author: "Elena R.",
    position: "Parent",
    location: "Parkwoods, 2024",
    portrait: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/parent_elena_portrait.webp",
    portraitSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/parent_elena_portrait.webp-wvc-srcset",
    portraitSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/parent_elena_portrait.webp-wvc-sizes"
  }];
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  useEffect(() => {
    const interval = setInterval(() => {
      handleChange((activeIndex + 1) % testimonials.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [activeIndex, testimonials.length]);
  const handleChange = (newIndex: number) => {
    if (newIndex === activeIndex) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex(newIndex);
      setIsTransitioning(false);
    }, 600);
  };
  const handlePrev = () => {
    handleChange((activeIndex - 1 + testimonials.length) % testimonials.length);
  };
  const handleNext = () => {
    handleChange((activeIndex + 1) % testimonials.length);
  };
  const current = testimonials[activeIndex];
  return <section data-section-id="318" id="voices-from-our-community" className="dark bg-background text-foreground relative overflow-hidden py-32 md:py-48 lg:py-[200px] px-6">
      {}
      <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722029/img/classroom_nature_backdrop.png)] opacity-[0.08]" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" aria-hidden="true" />

      <div ref={ref} className={`relative mx-auto max-w-5xl text-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="flex justify-center mb-12 md:mb-16">
          <Quote className="size-20 md:size-[120px] text-primary/30" strokeWidth={0.75} aria-hidden="true" />
        </div>

        {}
        <div className={`transition-all duration-700 ease-out ${isTransitioning ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}`}>
          {}
          <blockquote className="mx-auto max-w-[900px] px-2">
            <p className="font-default italic text-2xl md:text-3xl lg:text-[42px] leading-[1.4] text-foreground/95">
              &ldquo;{current.quote}&rdquo;
            </p>
          </blockquote>

          {}
          <div className="flex justify-center my-10 md:my-12">
            <div className="h-px w-[60px] bg-primary" aria-hidden="true" />
          </div>

          {}
          <div className="flex flex-col md:flex-row items-center justify-center gap-5 md:gap-6">
            <div className="size-16 rounded-lg overflow-hidden border border-primary/30 shrink-0">
              <img src={current.portrait} data-wvc-srcset={current.portraitSrcset} data-wvc-sizes={current.portraitSizes} alt={`Portrait of ${current.author}`} className="size-full object-cover" loading="lazy" decoding="async" />
            </div>
            <p className="font-default text-xs md:text-sm tracking-[0.25em] uppercase text-muted-foreground">
              <span className="text-foreground">{current.author}</span>
              <span className="mx-2 text-primary">&mdash;</span>
              <span>{current.location}</span>
            </p>
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-20 flex items-center justify-center gap-6">
          <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="text-muted-foreground hover:text-foreground hover:bg-transparent rounded-lg">
            <ChevronLeft className="size-5" strokeWidth={1.25} />
          </Button>

          <div className="flex items-center gap-3" role="tablist" aria-label="Testimonial pagination">
            {testimonials.map((_, i) => <Button key={i} data-index={i} onClick={() => handleChange(i)} aria-label={`Go to testimonial ${i + 1}`} aria-selected={i === activeIndex} role="tab" variant="ghost" className={`h-1.5 rounded-lg transition-all duration-500 px-0 ${i === activeIndex ? "w-8 bg-primary hover:bg-primary" : "w-1.5 bg-foreground/25 hover:bg-foreground/40"}`} />)}
          </div>

          <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Next testimonial" className="text-muted-foreground hover:text-foreground hover:bg-transparent rounded-lg">
            <ChevronRight className="size-5" strokeWidth={1.25} />
          </Button>
        </div>
      </div>
    </section>;
}